/**
 * "Send feedback…" (docs/specs/support.md): kind, text, one optional screenshot and diagnostics. Sending ensures my
 * DM with Support, uploads the screenshot into it, then posts the request (the API posts the card as me in that DM).
 * Over the form limit the API answers 429 with `retryAt`: the form keeps the text and Send stays off until then.
 * Portable: no DOM, messages are descriptors; the platform captures or picks the screenshot and shows its preview.
 */
import type { FeedbackKind, SupportDiagnostics, SupportRequestInput, SupportRequestResult } from "@bundle/contracts";
import type { MessageDescriptor } from "../i18n.ts";
import { checkScreenshot, checkText, counter } from "../logic/support.ts";
import { ApiFailure, type ApiClient, type Clock, type DataSource, type PickedFile } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";

export type FeedbackScreenshot = {
  file: PickedFile;
  /** A local preview the platform can show (web: an object URL); absent = a placeholder. */
  preview?: string;
  /** Not an image, or too big: shown next to the thumbnail, and Send stays off. */
  error?: MessageDescriptor;
};

export type FeedbackStatus = "idle" | "sending" | "rateLimited" | "error";

export type FeedbackState = {
  kind: FeedbackKind;
  text: string;
  includeDiagnostics: boolean;
  screenshot?: FeedbackScreenshot;
  status: FeedbackStatus;
  /** rateLimited: when sending is allowed again (ms). */
  retryAt?: number;
  /** The text is too short or too long (shown after a send attempt, cleared while typing). */
  textError?: MessageDescriptor;
  /** "3612 / 4000" near the limit, else absent. */
  counter?: string;
};

export type FeedbackDeps = {
  ws: string;
  api: Pick<ApiClient, "supportDm" | "supportRequest">;
  data: Pick<DataSource, "uploadFile">;
  /** What "Include diagnostics" sends, read when the form shows it or sends (current screen, connection). */
  diagnostics: () => SupportDiagnostics;
  clock: Clock;
};

/** Without a server `retryAt`, try again after an hour (the shortest form window). */
const DEFAULT_RETRY_MS = 60 * 60 * 1000;

function initial(kind: FeedbackKind = "bug"): FeedbackState {
  return { kind, text: "", includeDiagnostics: true, status: "idle" };
}

export class FeedbackViewModel extends ViewModel<FeedbackState> {
  private cancelRetry: (() => void) | undefined;

  constructor(private readonly deps: FeedbackDeps) {
    super(initial());
    this.onDispose(() => this.cancelRetry?.());
  }

  /** A fresh form (opening the sheet again). Keeps a running rate limit. */
  reset(kind: FeedbackKind = "bug"): void {
    const next = initial(kind);
    if (this.state.status === "rateLimited" && this.state.retryAt !== undefined) {
      next.status = "rateLimited";
      next.retryAt = this.state.retryAt;
    }
    this.setState(next);
  }

  setKind(kind: FeedbackKind): void {
    this.update({ kind });
  }

  setText(text: string): void {
    const next: FeedbackState = { ...this.state, text };
    delete next.textError;
    const c = counter(text);
    if (c === null) delete next.counter;
    else next.counter = c;
    if (next.status === "error") next.status = "idle";
    this.setState(next);
  }

  setIncludeDiagnostics(on: boolean): void {
    this.update({ includeDiagnostics: on });
  }

  /** A captured, picked or pasted screenshot replaces the previous one. */
  setScreenshot(file: PickedFile, preview?: string): void {
    const check = checkScreenshot(file);
    const shot: FeedbackScreenshot = { file };
    if (preview !== undefined) shot.preview = preview;
    if (!check.ok) shot.error = check.error;
    this.update({ screenshot: shot });
  }

  removeScreenshot(): void {
    if (this.state.screenshot === undefined) return;
    const next = { ...this.state };
    delete next.screenshot;
    this.setState(next);
  }

  /** The diagnostics as they would be sent now ("What's included"). */
  diagnostics(): SupportDiagnostics {
    return this.deps.diagnostics();
  }

  /** Send is pressable: not sending, not rate limited, no screenshot error. The text is checked on press. */
  get canSend(): boolean {
    const s = this.state;
    return s.status !== "sending" && s.status !== "rateLimited" && s.screenshot?.error === undefined;
  }

  /**
   * Sends the request. Resolves with the result (the form is cleared), or undefined when the text is invalid, a
   * limit applies or sending failed (the state says which; the text is kept).
   */
  async submit(): Promise<SupportRequestResult | undefined> {
    if (!this.canSend) return undefined;
    const s = this.state;
    const text = checkText(s.text);
    if (!text.ok) {
      this.update({ textError: text.error });
      return undefined;
    }
    this.update({ status: "sending" });
    try {
      const dm = await this.deps.api.supportDm(this.deps.ws);
      const input: SupportRequestInput = { kind: s.kind, text: s.text.trim() };
      if (s.screenshot !== undefined) {
        const stored = await this.deps.data.uploadFile(this.deps.ws, s.screenshot.file, { type: "channel", channelId: dm.channelId }).done;
        input.fileIds = [stored.id];
      }
      if (s.includeDiagnostics) input.diagnostics = this.deps.diagnostics();
      const result = await this.deps.api.supportRequest(this.deps.ws, input);
      this.setState(initial(s.kind));
      return result;
    } catch (e) {
      if (e instanceof ApiFailure && e.kind === "rateLimited") this.limit(e.retryAt ?? this.deps.clock.now() + DEFAULT_RETRY_MS);
      else this.update({ status: "error" });
      return undefined;
    }
  }

  private limit(retryAt: number): void {
    this.cancelRetry?.();
    this.update({ status: "rateLimited", retryAt });
    this.cancelRetry = this.deps.clock.setTimer(Math.max(0, retryAt - this.deps.clock.now()), () => {
      this.cancelRetry = undefined;
      if (this.state.status !== "rateLimited") return;
      const next: FeedbackState = { ...this.state, status: "idle" };
      delete next.retryAt;
      this.setState(next);
    });
  }
}
